/**
 * Expense Reports List Component
 *
 * Displays a list of expense report summaries with submitter info,
 * amount, status badge, and click handlers for viewing details.
 */

import type { ExpenseReportSummary } from '../types/expense-tracker';
import { createExpenseStatusBadge } from './expense-status-badge';
import { formatCurrency } from '../types/expense-tracker';

/**
 * Create an expense reports list component
 * @param reports - Array of expense report summaries to display
 * @param onSelect - Callback when a report is clicked
 * @returns HTMLElement containing the list
 */
export function createExpenseReportsList(
  reports: ExpenseReportSummary[],
  onSelect: (report: ExpenseReportSummary) => void
): HTMLElement {
  const container = document.createElement('div');
  container.className = 'space-y-2';

  if (reports.length === 0) {
    const empty = document.createElement('div');
    empty.className = 'text-center py-8 text-muted-foreground';
    empty.textContent = 'No expense reports submitted yet';
    container.appendChild(empty);
    return container;
  }

  for (const report of reports) {
    const row = document.createElement('div');
    row.className = `
      flex items-center justify-between p-3 rounded-lg
      bg-card border border-border
      hover:bg-accent hover:border-accent-foreground/20
      cursor-pointer transition-colors
    `;
    row.setAttribute('data-report-id', report.id);
    row.setAttribute('role', 'button');
    row.setAttribute('tabindex', '0');

    // Left side: Submitter info and submitted date
    const leftSection = document.createElement('div');
    leftSection.className = 'flex-1 min-w-0';

    // Submitter name (AC2 requirement)
    const submitterDiv = document.createElement('div');
    submitterDiv.className = 'text-sm font-medium truncate';
    submitterDiv.textContent = report.submitter_name || report.request_title;
    leftSection.appendChild(submitterDiv);

    const metaDiv = document.createElement('div');
    metaDiv.className = 'text-xs text-muted-foreground mt-1';

    const submittedDate = report.submitted_at
      ? new Date(report.submitted_at).toLocaleDateString('en-CH', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })
      : 'Not submitted';

    // Show request title and receipt count
    metaDiv.textContent = `${report.request_title} • ${report.receipt_count} receipt(s) • ${submittedDate}`;
    leftSection.appendChild(metaDiv);

    row.appendChild(leftSection);

    // Right side: Amount and status badge
    const rightSection = document.createElement('div');
    rightSection.className = 'flex items-center gap-4 ml-4';

    const amountSpan = document.createElement('span');
    amountSpan.className = 'text-sm font-medium whitespace-nowrap';
    amountSpan.textContent = formatCurrency(report.total_amount_chf, 'CHF');
    rightSection.appendChild(amountSpan);

    const statusBadge = createExpenseStatusBadge(report.status, { size: 'sm' });
    rightSection.appendChild(statusBadge);

    row.appendChild(rightSection);

    // Click handler
    row.addEventListener('click', () => onSelect(report));

    // Keyboard accessibility
    row.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(report);
      }
    });

    container.appendChild(row);
  }

  return container;
}

export default createExpenseReportsList;
